import type { Action } from '@/config/types/Action'
import type { Plan } from '@/config/types/Plan'

export interface FilterState {
  searchQuery: string
  selectedStates: string[]
  selectedKpiAreas: string[]
  selectedDueDateFilters: string[]
  selectedRetailers?: (string | number)[]
}

export interface ActionFiltersComposable {
  isOverdue: (action: Action) => boolean
  isDueSoon: (action: Action) => boolean
  isDueLater: (action: Action) => boolean
  formatShort: (value: string | Date) => string
  filterActions: (actions: Action[], filterState: FilterState, plans?: Plan[]) => Action[]
}

/**
 * Check if an action is overdue
 * Centralized logic - use this instead of duplicating in components
 */
export const isActionOverdue = (action: Action): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dueDate = new Date(action.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  return dueDate < today && action.state !== 'complete'
}

export default function useActionFilters(): ActionFiltersComposable {
  const isOverdue = (action: Action): boolean => {
    const today = new Date()
    const dueDate = new Date(action.dueDate)
    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    return dueDate < today && action.state !== 'complete'
  }

  const isDueSoon = (action: Action): boolean => {
    if (action.state === 'complete') return false

    const today = new Date()
    const dueDate = new Date(action.dueDate)
    const sevenDaysFromNow = new Date(today)
    sevenDaysFromNow.setDate(today.getDate() + 7)

    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    sevenDaysFromNow.setHours(0, 0, 0, 0)

    return dueDate >= today && dueDate <= sevenDaysFromNow
  }

  const isDueLater = (action: Action): boolean => {
    if (action.state === 'complete') return false

    const today = new Date()
    const dueDate = new Date(action.dueDate)
    const sevenDaysFromNow = new Date(today)
    sevenDaysFromNow.setDate(today.getDate() + 7)

    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    sevenDaysFromNow.setHours(0, 0, 0, 0)

    return dueDate > sevenDaysFromNow
  }

  const formatShort = (value: string | Date): string => {
    const d = value instanceof Date ? value : new Date(value)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  const filterActions = (actions: Action[], filterState: FilterState, plans?: Plan[]): Action[] => {
    let filtered = actions

    if (filterState.searchQuery.trim()) {
      const query = filterState.searchQuery.toLowerCase().trim()
      filtered = filtered.filter((action) => action.title.toLowerCase().includes(query))
    }

    if (filterState.selectedStates.length > 0) {
      filtered = filtered.filter((action) => filterState.selectedStates.includes(action.state))
    }

    if (filterState.selectedKpiAreas.length > 0) {
      filtered = filtered.filter(
        (action) => action.kpiArea && filterState.selectedKpiAreas.includes(action.kpiArea),
      )
    }

    if (filterState.selectedDueDateFilters.length > 0) {
      filtered = filtered.filter((action) => {
        if (filterState.selectedDueDateFilters.includes('overdue') && isOverdue(action)) return true
        if (filterState.selectedDueDateFilters.includes('due-soon') && isDueSoon(action))
          return true
        if (filterState.selectedDueDateFilters.includes('due-later') && isDueLater(action))
          return true
        return false
      })
    }

    if (filterState.selectedRetailers && filterState.selectedRetailers.length > 0 && plans) {
      const selectedRetailerIds = new Set(filterState.selectedRetailers)
      filtered = filtered.filter((action) => {
        const plan = plans.find((p) => p.actions.some((a) => a.id === action.id))
        if (!plan || !plan.retailers || plan.retailers.length === 0) return false
        return plan.retailers.some((retailer) => selectedRetailerIds.has(retailer.id))
      })
    }

    return filtered
  }

  return {
    isOverdue,
    isDueSoon,
    isDueLater,
    formatShort,
    filterActions,
  }
}
